import { TServiceVariant } from './../lib/types'
import { usePathname } from 'next/navigation'
import { useEffect, useMemo, useState } from 'react'
import { useDispatch } from 'react-redux'

import { addProductInLocalList, removeItemInLocalList } from 'store/slices/localProductsList'

import { useCart } from '@/entities/cart'

import { useGetRelatedProducts } from '@/shared/lib/hooks/useGetRelatedProducts'
import { ProductType } from '@/shared/types/deprecated-types'

export const useProductServices = (rows: TServiceVariant[], relatedId: number) => {
	const dispatch = useDispatch()
	const { addItem, deleteItem, changeCount, getBasketItem } = useCart()
	const isBasket = usePathname() === `/cart`
	const isRelatedProductInCart = useMemo(() => getBasketItem(relatedId), [getBasketItem, relatedId])
	const cartItemElements = useGetRelatedProducts(relatedId)
	const [activeServiceId, setActiveServiceId] = useState<string | undefined>(undefined)

	useEffect(() => {
		if (!cartItemElements?.length) return

		const filterItems = cartItemElements.filter(item =>
			rows.some(row => !!item.ID && row.ID === item.ID.toString() && item.RELATED_PRODUCTS === relatedId)
		)

		if (!filterItems.length) return

		filterItems.forEach(item => {
			const { ID } = item

			if (!!ID && ID.toString() !== activeServiceId) {
				setActiveServiceId(ID.toString())
			}
		})
	}, [cartItemElements])

	const countChangeHandler = (newCount: number, service: TServiceVariant): void => {
		const basketItem = cartItemElements.find(item => !!item.ID && item.ID.toString() === service.ID)

		if (isRelatedProductInCart && isRelatedProductInCart.QUANTITY && isRelatedProductInCart.QUANTITY < newCount) return

		if (basketItem) {
			changeCount(basketItem.ID_BASKET || 0, newCount, basketItem.ID, { updateBasket: isBasket })
		}
	}

	const activityChangeHandler = async (isActive: boolean, service: TServiceVariant) => {
		if (isActive) {
			enableService(service, isBasket, false)

			if (activeServiceId) {
				disableService(activeServiceId, isBasket)
			}
			setActiveServiceId(service.ID)
		} else {
			await disableService(service.ID, isBasket)
			setActiveServiceId(undefined)
		}
	}

	const enableService = (service: TServiceVariant, updateBasket: boolean, blockUpdate = false) => {
		const payload = {
			ID: Number(service.ID),
			RELATED_PRODUCTS: relatedId,
			TYPE_PRODUCTS: ProductType.Service,
			PRICE: { VALUE: service.PRICE },
			TITLE: service.TITLE,
			KOD: service.KOD,
			SECTION_ID: service.SECTION_ID,
			updateBasket,
			blockUpdate
		}

		if (isRelatedProductInCart) {
			// @ts-ignore
			addItem(payload)
		} else {
			// @ts-ignore
			dispatch(addProductInLocalList(payload))
		}
	}

	const disableService = (disableId: string, updateBasket: boolean) => {
		const basketItem = cartItemElements.find(
			item => !!item.ID && item.ID.toString() === disableId && item.RELATED_PRODUCTS === relatedId
		)

		if (!basketItem) return

		if (isRelatedProductInCart) {
			deleteItem(basketItem.ID_BASKET || 0, basketItem.ID, { updateBasket })
		} else {
			dispatch(removeItemInLocalList(basketItem))
		}
	}

	const hasActiveService = !!activeServiceId

	return {
		countChangeHandler,
		activityChangeHandler,
		hasActiveService,
		activeServiceId
	}
}
